import { useMemo, useState } from "react";
import { Check, Printer } from "lucide-react";
import { STATUS_LABELS } from "../../lib/api";
import { itemsLabel } from "../../lib/products";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });

const Tick = ({ o, onStatus }) => {
  const done = o.status === "delivered";
  return (
    <button
      onClick={() => !done && window.confirm(`Mark ${o.ref} delivered and email ${o.name}?`) && onStatus(o.id, "delivered")}
      disabled={done}
      aria-label={done ? "Delivered" : "Mark delivered"}
      className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${done ? "border-emerald-400 bg-emerald-400 text-[#0F1410]" : "border-white/40 hover:border-[var(--gold)] hover:bg-[var(--gold)]/20"}`}
      data-testid={`runsheet-tick-${o.ref}`}
    >
      {done && <Check className="h-4 w-4" />}
    </button>
  );
};

const SuburbGroup = ({ suburb, orders, onStatus }) => (
  <section className="run-group mb-8 break-inside-avoid" data-testid={`runsheet-suburb-${suburb}`}>
    <h3 className="mb-3 flex items-baseline justify-between border-b border-white/15 pb-2 font-display text-2xl font-extrabold">
      {suburb}
      <span className="font-mono text-xs font-normal text-[var(--sage)]">{orders.reduce((a, o) => a + (o.quantity ?? 0), 0)} items</span>
    </h3>
    {orders.map((o) => (
      <div key={o.id} className={`grid grid-cols-[1.5rem_1fr] gap-3 border-b border-white/5 py-3 transition-opacity sm:grid-cols-[1.5rem_1fr_1fr_auto] ${o.status === "delivered" ? "opacity-50" : ""}`} data-testid={`runsheet-row-${o.ref}`}>
        <Tick o={o} onStatus={onStatus} />
        <div>
          <p className="font-semibold">{o.name} <span className="font-mono text-xs text-[var(--gold)]">{o.ref}</span></p>
          <p className="text-[var(--sage)]">{o.address}</p>
          <p className="text-[var(--sage)]">{o.phone}</p>
        </div>
        <p className="col-start-2 text-sm italic text-[var(--sage)] sm:col-start-auto">{o.notes || "—"}</p>
        <div className="col-start-2 text-left sm:col-start-auto sm:text-right">
          <div className="font-display text-base font-bold leading-snug">{itemsLabel(o).map((l) => <p key={l}>{l}</p>)}</div>
          <p className={`text-xs ${o.status === "pending_payment" ? "font-bold text-amber-300" : "text-[var(--sage)]"}`}>{STATUS_LABELS[o.status] ?? o.status}</p>
        </div>
      </div>
    ))}
  </section>
);

export const RunSheet = ({ orders, onStatus }) => {
  const dates = useMemo(() => [...new Set(orders.filter((o) => o.confirmed_delivery_date && o.status !== "cancelled").map((o) => o.confirmed_delivery_date))].sort(), [orders]);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(dates.find((d) => d >= today) ?? dates[dates.length - 1] ?? today);

  const groups = useMemo(() => {
    const day = orders.filter((o) => o.confirmed_delivery_date === date && o.status !== "cancelled");
    const by = {};
    day.forEach((o) => { (by[o.suburb?.trim() || "Other"] ??= []).push(o); });
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [orders, date]);
  const total = groups.reduce((a, [, os]) => a + os.reduce((s, o) => s + (o.quantity ?? 0), 0), 0);

  return (
    <div className="mt-10" data-testid="runsheet">
      <div className="no-print mb-8 flex flex-wrap items-center gap-3">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="field [color-scheme:dark]" style={{ width: "auto" }} data-testid="runsheet-date-input" />
        {dates.map((d) => (
          <button key={d} onClick={() => setDate(d)} className={`rounded-full border px-4 py-2 text-sm ${d === date ? "border-[var(--gold)] text-[var(--gold)]" : "border-white/10 text-[var(--sage)]"}`} data-testid={`runsheet-date-chip-${d}`}>
            {new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" })}
          </button>
        ))}
        <button onClick={() => window.print()} className="btn-gold ml-auto px-5 py-3 text-sm" data-testid="runsheet-print-button"><Printer className="h-4 w-4" /> Print</button>
      </div>
      <div className="print-area rounded-3xl border border-white/10 p-6 sm:p-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--gold)]">Go Box run sheet</p>
            <h2 className="mt-2 font-display text-4xl font-extrabold" data-testid="runsheet-title">{fmt(date)}</h2>
          </div>
          <p className="font-display text-2xl font-extrabold" data-testid="runsheet-total">{total} items · {groups.reduce((a, [, os]) => a + os.length, 0)} stops</p>
        </div>
        {groups.length === 0 ? (
          <p className="py-10 text-center text-[var(--sage)]" data-testid="runsheet-empty">No deliveries booked for this day. Set delivery dates on the Orders tab.</p>
        ) : groups.map(([s, os]) => <SuburbGroup key={s} suburb={s} orders={os} onStatus={onStatus} />)}
      </div>
    </div>
  );
};
